import { type KeyboardEvent } from 'react';

import { Container } from '@/components/legacy-ui';
import {
  AI_CREDIT_ACTIONS,
  AI_CREDIT_PACKS,
  AICreditPackId,
} from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';

interface AICreditsProps {
  selectedPack: AICreditPackId;
  onPackChange: (pack: AICreditPackId) => void;
}

export function AICreditsSection({
  selectedPack,
  onPackChange,
}: AICreditsProps) {
  const selected =
    AI_CREDIT_PACKS.find(p => p.id === selectedPack) || AI_CREDIT_PACKS[0];

  return (
    <section className="space-y-4">
      <h3 className="text-chariot-text-primary text-base font-semibold">
        Guard Intelligence — AI Credit Packs
      </h3>

      {/* Pack selector - radio cards */}
      <div
        role="radiogroup"
        aria-label="AI Credit Pack"
        className="grid grid-cols-5 gap-3"
      >
        {AI_CREDIT_PACKS.map(pack => {
          const isSelected = selectedPack === pack.id;
          return (
            <Container
              key={pack.id}
              onClick={() => onPackChange(pack.id)}
              className={cn('relative cursor-pointer py-3 text-center')}
              isSelected={isSelected}
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onKeyDown={(e: KeyboardEvent) => {
                const packs = AI_CREDIT_PACKS;
                const currentIndex = packs.findIndex(
                  p => p.id === selectedPack
                );
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  const nextIndex = (currentIndex + 1) % packs.length;
                  onPackChange(packs[nextIndex].id);
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const prevIndex =
                    (currentIndex - 1 + packs.length) % packs.length;
                  onPackChange(packs[prevIndex].id);
                } else if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onPackChange(pack.id);
                }
              }}
              data-testid={`ai-pack-${pack.id}`}
            >
              {isSelected && (
                <div className="bg-chariot-brand-primary absolute -top-2.5 left-1/2 -translate-x-1/2 transform rounded-full px-2 py-0.5 text-[10px] font-bold text-white">
                  SELECTED
                </div>
              )}
              <div className="text-sm font-medium">{pack.label}</div>
              <div className="text-chariot-text-secondary text-xs">
                {pack.creditsPerMonth > 0
                  ? `${pack.creditsPerMonth.toLocaleString()} credits/mo`
                  : 'No AI Credits / BYOAI'}
              </div>
              <div className="text-chariot-brand-primary mt-1 text-sm font-bold">
                ${pack.annualPrice.toLocaleString()}/yr
              </div>
            </Container>
          );
        })}
      </div>

      {/* Credit consumption reference - compact 2-column grid */}
      <div>
        <h4 className="text-chariot-text-primary mb-3 text-sm font-semibold">
          Credit Consumption
        </h4>
        <div className="flex flex-col">
          {AI_CREDIT_ACTIONS.map(item => (
            <div
              key={item.action}
              className="border-chariot-stroke/50 flex items-center justify-between border-b py-2"
              title={item.description}
            >
              <span className="text-chariot-text-primary text-base">
                {item.action}
              </span>
              <span className="text-chariot-text-primary ml-2 font-mono text-sm font-semibold tabular-nums">
                {item.credits.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Selected pack summary */}
      <div className="border-chariot-stroke flex items-center justify-between border-t pt-4">
        <p className="text-chariot-text-tertiary text-xs font-semibold tracking-wide uppercase">
          AI Credits Annual Cost
        </p>
        <div className="text-chariot-text-primary text-right">
          <div className="text-2xl font-bold">
            ${selected.annualPrice.toLocaleString()}
          </div>
          <div className="text-chariot-text-secondary text-xs">
            {selected.creditsPerMonth.toLocaleString()} credits/month
          </div>
        </div>
      </div>
    </section>
  );
}
